import type { CSSProperties } from 'react';
import { Lien } from '../../app/navigation.js';
import { CIBLE_MIN, COULEURS } from '../../app/styles.js';
import { avecDossier, CHEMINS_VOISINS } from './chemins.js';

/**
 * Sections du dossier portées par D03 (disposition de la démo : /dossiers/:id/…). Deux sortes de sections : un écran voisin
 * (`chemin`) ou une sous-vue de D03 (`vue`, adresse `/dossier?dossier=…&vue=…`).
 * PARITE-4 : « Actes notariés », « Comptes rendus » et « Journal » sont des sous-vues (aucun écran nouveau au contrat : les écrans suivent
 * les maquettes).
 * PARITE-5 : « Import de contacts » (démo brigitte/ImportContacts) est une sous-vue, juste après les héritiers ; « Vidéos »
 * (démo facturation-ged/VideosDossier) suit « Documents ».
 */
export type VueDossier = 'ensemble' | 'comptes-rendus' | 'actes' | 'journal' | 'import-contacts' | 'videos';

interface Section {
  cle: string;
  libelle: string;
  chemin?: string;
  vue?: Exclude<VueDossier, 'ensemble'>;
}

export const SECTIONS_DOSSIER: readonly Section[] = [
  { cle: 'ensemble', libelle: 'Vue d’ensemble' },
  { cle: 'demarrage', libelle: 'Démarrage guidé', chemin: CHEMINS_VOISINS.demarrage.chemin },
  { cle: 'arbre', libelle: 'Arbre et tableaux', chemin: CHEMINS_VOISINS.arbre.chemin },
  { cle: 'heritiers', libelle: 'Héritiers', chemin: CHEMINS_VOISINS.heritiers.chemin },
  { cle: 'import-contacts', libelle: 'Import de contacts', vue: 'import-contacts' },
  { cle: 'biens', libelle: 'Biens et titres', chemin: CHEMINS_VOISINS.biens.chemin },
  { cle: 'actes', libelle: 'Actes notariés', vue: 'actes' },
  { cle: 'documents', libelle: 'Documents', chemin: CHEMINS_VOISINS.documents.chemin },
  { cle: 'videos', libelle: 'Vidéos', vue: 'videos' },
  { cle: 'messages', libelle: 'Messages', chemin: CHEMINS_VOISINS.messages.chemin },
  { cle: 'facturation', libelle: 'Facturation', chemin: CHEMINS_VOISINS.facturation.chemin },
  { cle: 'estimation', libelle: 'Estimation', chemin: CHEMINS_VOISINS.estimation.chemin },
  { cle: 'comptes-rendus', libelle: 'Comptes rendus', vue: 'comptes-rendus' },
  { cle: 'journal', libelle: 'Journal', vue: 'journal' },
];

/** Vues reconnues dans l'adresse ; toute autre valeur retombe sur la vue d'ensemble (jamais de page vide). */
export function vueDepuisAdresse(recherche: string): VueDossier {
  const vue = new URLSearchParams(recherche).get('vue');
  return SECTIONS_DOSSIER.some((s) => s.vue !== undefined && s.vue === vue) ? (vue as VueDossier) : 'ensemble';
}

export const cheminVue = (dossierId: string, vue: VueDossier): string =>
  `/dossier?dossier=${encodeURIComponent(dossierId)}${vue === 'ensemble' ? '' : `&vue=${vue}`}`;

const cibleLien: CSSProperties = { display: 'inline-flex', alignItems: 'center', minHeight: CIBLE_MIN, textDecoration: 'none' };
const lienNav: CSSProperties = { ...cibleLien, padding: '0 14px', color: COULEURS.ardoise };
const lienCourant: CSSProperties = {
  ...cibleLien,
  padding: '0 14px',
  borderBottom: `3px solid ${COULEURS.boutonPrincipal}`,
  fontWeight: 600,
  color: 'inherit',
};

/**
 * Navigation « Sections du dossier » : la section courante est marquée, chaque lien garde le dossier ouvert.
 * Sur mobile (`defilant`, juge du 30/10, U7), les sections tiennent sur une ligne qui défile horizontalement au lieu d'occuper six lignes.
 */
export function NavSections({ dossierId, actif, defilant = false }: { dossierId: string; actif: VueDossier; defilant?: boolean }) {
  const disposition: CSSProperties = defilant ? { flexWrap: 'nowrap', overflowX: 'auto' } : { flexWrap: 'wrap' };
  const ligne: CSSProperties = defilant ? { whiteSpace: 'nowrap', flexShrink: 0 } : {};
  return (
    <nav aria-label="Sections du dossier" style={{ display: 'flex', ...disposition, gap: 8, borderBottom: '1px solid #E2E1EA', fontSize: 14 }}>
      {SECTIONS_DOSSIER.map((s) => {
        const courante = (s.cle === 'ensemble' && actif === 'ensemble') || (s.vue !== undefined && s.vue === actif);
        if (courante) {
          return (
            <a key={s.cle} href="#" aria-current="page" style={{ ...lienCourant, ...ligne }}>
              {s.libelle}
            </a>
          );
        }
        const vers = s.chemin ? avecDossier(s.chemin, dossierId) : cheminVue(dossierId, s.vue ?? 'ensemble');
        return <Lien key={s.cle} vers={vers} enfants={s.libelle} style={{ ...lienNav, ...ligne }} />;
      })}
    </nav>
  );
}
